import {
  getCashflowModelScenarioApi,
  getCashflowModelMarketingVectorApi,
  updateCashflowModelScenarioApi,
} from 'api/cashflowModelApi';
import CustomerSelect from 'components/cockpit/CustomerSelect';
import FileInput from 'components/inputs/FileInput';
import {
  AnotherNumberInput,
  CurrencyNumberInput,
  PercentageNumberInput,
  TwNumberInput,
} from 'components/NumberInput';
import { firstOfMonthIn, monthName } from 'helpers/dateHelper';
import { renderInputElement } from 'helpers/formHelper';
import { hasObjectAllValues, isObjectEmpty } from 'helpers/objectHelper';
import cloneDeep from 'lodash.clonedeep';
import Papa from 'papaparse';
import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
// import debounce from "lodash.debounce";

import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage, Icon,
  Link,
  Table, Tbody, Td,
  Text, Th, Thead, Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { numberFormat, percentageFormat } from 'components/Number';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import { HelperTooltip } from 'components/Tooltip';
import { generateSelectOptions } from 'helpers/inputHelper';
import PropTypes from 'prop-types';
import CashflowModelDrivers from './CashflowModelDrivers';
import { parseAccountingCsv, parseCohortsCsv } from './csvParsers';
import ScenarioSelector from './ScenarioSelector';
import SelectInput from "../../../components/inputs/SelectInput";
import FieldInput from "../../../components/fields/FieldInput";
import {TwDatePicker} from "../../../components/DatePicker";
import moment from "moment/moment";
import {BUSINESS_MODEL_TYPE_META} from "../../../constants/underwritingConstants";

const MONTHS_COUNT = 12

const SECTION_KEY = {
  INPUT: 'input',
  INPUT2: 'input2',
  MARKETING_AND_FUNDING: 'marketingAndFunding',
  REPAYMENT_TERMS: 'repaymentTerms',
  TRUE_UP: 'trueUp',
};

const SECTION_CONFIG = [
  {
    title: 'Input',
    key: SECTION_KEY.INPUT,
  },
  {
    title: '',
    key: SECTION_KEY.INPUT2,
  },
  {
    title: 'Marketing & Funding',
    key: SECTION_KEY.MARKETING_AND_FUNDING,
  },
  {
    title: 'Repayment Terms',
    key: SECTION_KEY.REPAYMENT_TERMS,
  },
  {
    title: 'True Up Values (Enter percentages only)',
    key: SECTION_KEY.TRUE_UP,
  },
];

const SCENARIO_TYPE = {
  DEFAULT: 'default',
  BULL: 'bull',
  BEAR: 'bear',
  OTHER: 'other',
};

const SCENARIO_TYPE_META = {
  [SCENARIO_TYPE.DEFAULT]: {
    label: 'Default',
  },
  [SCENARIO_TYPE.BULL]: {
    label: 'Bull',
  },
  [SCENARIO_TYPE.BEAR]: {
    label: 'Bear',
  },
  [SCENARIO_TYPE.OTHER]: {
    label: 'Other',
  },
};

const SCENARIO_TYPE_OPTIONS = generateSelectOptions({
  metaObj: SCENARIO_TYPE_META,
});


const SectionFields = ({ title, fields, sectionKey, control, errors, setValue }) => {

  const [showMore, setShowMore] = useState(false);

  const toggleRows = () => {
    setShowMore((prev) => !prev);
  };

  const elementRenderer = (field, index) => {
    return (
        <Fragment key={index}>
          <Box>
            <Flex align={'center'}>
              <Text>{field.label}</Text>
              {field.tooltip && <HelperTooltip content={field.tooltip} />}
            </Flex>

            <Box mt={1}>
              {field.isCustomField ? (
                  field.element
              ) : (
                  <Box w={field.width || 200}>
                    <FormControl isInvalid={errors[field.name]}>
                      <Controller
                          key={index}
                          control={control}
                          name={field.name}
                          render={renderInputElement({
                            item: field,
                            showPrefix: true,
                            onChange: (name, value) => {
                              if (field.onChange) {
                                field.onChange(name, value, fields);
                              } else {
                                setValue(name, value);
                              }
                            },
                          })}
                          rules={
                              field.validate && {
                                validate: field.validate,
                              }
                          }
                      />
                      <FormErrorMessage>
                        {errors[field.name]?.message}
                      </FormErrorMessage>
                    </FormControl>
                  </Box>
              )}
            </Box>
          </Box>
        </Fragment>
    );
  }

  return (
    <VStack spacing={2} align={'start'}>
      <Text fontWeight={800}>{title}</Text>
      <Flex gap={10} fontSize={'sm'} hidden={sectionKey !== SECTION_KEY.MARKETING_AND_FUNDING}>
        <Table
        >
          <Thead>
            <Tr>
              <Td></Td>
              <Td>
                {fields.filter((field) => field.name === 'mfFactor').map(elementRenderer)}
              </Td>
              <Td>
                {fields.filter((field) => field.name === 'mfDefaultPercent').map(elementRenderer)}
              </Td>
              <Td></Td>
              <Td></Td>
              <Td>
                {fields.filter((field) => field.name === 'mfRevenueFactor').map(elementRenderer)}
              </Td>
            </Tr>
            <Tr>
              <Th>Month</Th>
              <Th>Marketing</Th>
              <Th>% of Marketing</Th>
              <Th>Funding</Th>
              <Th>% Revenue Share</Th>
              <Th>% Revenue Share To Collect</Th>
            </Tr>
          </Thead>
          <Tbody>
            {[...Array(MONTHS_COUNT).keys()].map((i) => (
            <>
              {(showMore || i < 3) && (
              <Tr key={i}>
                <Td>
                  {`${monthName(firstOfMonthIn(i))} (Mob ${i})`}
                </Td>
                <Td>
                  {fields.filter((field) => field.name === `mob${i}Marketing`).map(elementRenderer)}
                </Td>
                <Td>
                  {fields.filter((field) => field.name === `mob${i}PercentOfMarketing`).map(elementRenderer)}
                </Td>
                <Td>
                  {fields.filter((field) => field.name === `mob${i}Funding`).map(elementRenderer)}
                </Td>
                <Td>
                  {fields.filter((field) => field.name === `mob${i}RevenueShare`).map(elementRenderer)}
                </Td>
                <Td>
                  {fields.filter((field) => field.name === `mob${i}RevenueShareToCollect`).map(elementRenderer)}
                </Td>
              </Tr>
              )}
            </>
            ))
          }
          </Tbody>
        </Table>
      </Flex>
      <Flex gap={10} fontSize={'sm'} hidden={sectionKey !== SECTION_KEY.MARKETING_AND_FUNDING}>
        <Link color="blue.500" mt={2} onClick={toggleRows}>
          <Icon viewBox="0 0 24 24" boxSize={4}>
            <path
                fill="currentColor"
                d={
                  showMore
                      ? 'M12 8l-6 6h12l-6-6z' // Up arrow
                      : 'M12 16l6-6H6l6 6z' // Down arrow
                }
            />
          </Icon>
          { showMore ? 'Show fewer months' : 'Show more months'}
        </Link>

      </Flex>
      <Flex gap={10} wrap="wrap" maxW="1200px" fontSize={'sm'} hidden={sectionKey !== SECTION_KEY.TRUE_UP}>
        {fields
            .filter((field) => field.section === sectionKey)
            .map(elementRenderer)}
      </Flex>
      <Flex gap={10} fontSize={'sm'} hidden={!(sectionKey === SECTION_KEY.INPUT || sectionKey === SECTION_KEY.INPUT2 || sectionKey === SECTION_KEY.REPAYMENT_TERMS)}>
        {fields
          .filter((field) => field.section === sectionKey)
          .map(elementRenderer)}
      </Flex>
    </VStack>
  );
};

const CashflowModelInputs = forwardRef(
  ({ onSubmit, modelResults, roasVector, onCustomerChange }, ref) => {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [parseCohortsError, setParseCohortsError] = useState(null);
    const [parseAccountingError, setParseAccountingError] = useState(null);
    const [cohortFile, setCohortFile] = useState(null);
    const [accountingFile, setAccountingFile] = useState(null);
    const [drivers, setDrivers] = useState(null);
    const [selectedScenarioType, setSelectedScenarioType] = useState(null);
    const [scenario, setScenario] = useState(null);
    const [comments, setComments] = useState(null);
    const [useScenarioForDrivers, setUseScenarioForDrivers] = useState(false);
    // const [date, setDate] = useState(moment().format('YYYY-MM-DD'));

    const refDrivers = useRef(null);

    const {
      register,
      handleSubmit,
      reset,
      control,
      watch,
      setValue,
      getValues,
      formState: {errors},
    } = useForm();

    const formValues = watch();

    const getTwDatePicker = ({
                               minDate = null,
                               maxDate = null,
                               onChange = () => null,
                               value = null,
                             }) => {
      return (/*props*/) => {
        const r = (
            <TwDatePicker
                // {...props}
                value={value}
                onChange={onChange}
                minDate={minDate}
                maxDate={maxDate}
            />
        );
        return r;
      }
    }

    function getRoasAvgByOptions() {
      let r = Array.from({length: 12}, (_, i) => ({
        label: `${(i + 1)}`,
        value: (i + 1),
      }));
      return r;
    }

    function getMobMonthOptions() {
      let r = Array.from({length: 5}, (_, i) => {
        const mOffset = (i + 2)
        const mName = monthName(firstOfMonthIn((mOffset * -1)));
        let r2 = {
          label: `${mName} (Mob ${mOffset * -1})`,
          value: mOffset,
        };
        return r2;
      });
      return r;
    }

    function getNumOfMonthsForCalculationsOptions() {
      let r = Array.from({length: 5}, (_, i) => ({
        label: `${(i + 2)}`,
        value: (i + 2),
      }));
      return r;
    }

    function getPredictionSizeOptions() {
      let r = Array.from({length: 13}, (_, i) => ({
        label: `${(i + 12)}`,
        value: (i + 12),
      }));
      return r;
    }

    function calculateTrueUp(name, value) {
      const isTrueUp = getValues('isTrueUp')
      const numberOfRepayments = getValues('numberOfRepayments')
      if (!!numberOfRepayments && isTrueUp) {
        if (name === 'isTrueUp' || name === 'numberOfRepayments') {
          [...Array(numberOfRepayments - 1 || 0).keys()].map((i) => {
            setValue(`trueUpPayment${i}`, 0)
          })
          setValue(`trueUpPayment${numberOfRepayments - 1}`, 1)
        }
        // if some other trueUp payment has changed, validate it
        if (name.startsWith('trueUpPayment')) {
          // if last trueUp element has changed
          if (name === `trueUpPayment${numberOfRepayments - 1}`) {
            if (value !== 1) {
              errors[name] = {'message': 'Last TrueUp payment value should be 100'};
              return true;
            }
          }
          // if some other trueUp payment has changed, validate it
          if (name.startsWith('trueUpPayment')) {
            if (value !== 0) {
              if (value < 0 || value > 1) {
                errors[name] = {'message': 'Number should be non-negative and less or equal to 100'};
                return true
              } else {
                // positive should be more than last positive value
                const i = Number(name.substring(13));
                // validating self
                for (let j = i - 1; j >= 0; j--) {
                  const prevValue = getValues(`trueUpPayment${j}`);
                  if (!prevValue) {
                    continue;
                  } else if (value < prevValue) {
                    errors[name] = {'message': 'Value should be more than last previous non-zero value'};
                    return true;
                  } else {
                    // we found prev value that is less than current value, so we can stop
                    break;
                  }
                }
                // validating next values
                for (let j = i + 1; j < numberOfRepayments; j++) {
                  const nextName = `trueUpPayment${j}`
                  const nextValue = getValues(nextName);
                  if (!nextValue) {
                    continue;
                  } else if (value > nextValue) {
                    errors[nextName] = {'message': 'Value should be more than last previous non-zero value'};
                    return true;
                  } else {
                    // we found next value that is more than current value, so we can stop
                    delete errors[nextName];
                    break;
                  }
                }
              }
            }
          }
        }
      }
      delete errors[name];
      return true;
    }

    let allFields = [
      {
        label: 'Customer',
        section: SECTION_KEY.INPUT,
        name: 'customerId',
        isCustomField: true,
        required:
          (formValues.roasCohorts && !formValues.accounting) ||
          (formValues.accounting && !formValues.roasCohorts),
        element: (
          <>
            <Box w={200} whiteSpace={'nowrap'}>
              <CustomerSelect
                customerId={formValues.customerId}
                onChange={(customerId) => {
                  setSelectedScenarioType(null);
                  setValue('customerId', customerId);
                }}
              />
            </Box>
          </>
        ),
      },
      {
        label: 'ROAS Cohorts CSV',
        tooltip: 'Use 13 months of cohorts, starting from the month before the current one a year ago, up to the month before the current one (e.g., May 2024 to May 2025 if today is June 2025)',
        name: 'roasCohorts',
        section: SECTION_KEY.INPUT,
        isCustomField: true,
        required: !formValues.customerId,
        element: (
          <Flex gap={1} direction={'column'}>
            <FileInput
              initialFile={cohortFile}
              allowedFileTypes={['text/csv']}
              onSelectFiles={(files) => {
                let file = null
                if (files.length > 0) {
                    file = files[0];
                }
                setCohortFile(file);
                onSelectCohortsCsv(file);
              }}
            />
            <Link
              href="csv/cashflow-model-cohorts-example.csv"
              fontSize={'xs'}
              variant={'underline'}
            >
              Example File
            </Link>
            {parseCohortsError && (
              <Text color={'red.500'}>{parseCohortsError}</Text>
            )}
          </Flex>
        ),
      },
      {
        label: 'Accounting',
        name: 'accounting',
        section: SECTION_KEY.INPUT,
        isCustomField: true,
        element: (
          <Flex gap={1} direction={'column'}>
            <FileInput
              initialFile={accountingFile}
              allowedFileTypes={['text/csv']}
              onSelectFiles={(files) => {
                setAccountingFile(files[0]);
                onSelectAccountingCsv(files[0]);
              }}
            />
            <Link
              href="csv/cashflow-model-accounting-example.csv"
              fontSize={'xs'}
              variant={'underline'}
            >
              Example File
            </Link>
            {parseAccountingError && (
              <Text color={'red.500'}>{parseAccountingError}</Text>
            )}
          </Flex>
        ),
      },
      {
        label: 'Cash Override',
        section: SECTION_KEY.INPUT,
        name: 'cashOverride',
        element: CurrencyNumberInput,
        formatCsv: numberFormat,
        isInCsv: true,
        tooltip:
          'This will override the cash value for the last month of actual data and will be used as the starting point for cash prediction calculations.',
      },
      // {
      //   label: 'Select Date (Current Month Only)',
      //   section: SECTION_KEY.INPUT2,
      //   name: 'currentMonthDate',
      //   required: true,
      //   placeholder: 'Pick a date',
      //   element: getTwDatePicker({
      //     minDate: moment().startOf('month').toDate(),
      //     maxDate: moment().toDate(),
      //     onChange: (date) => {
      //       setValue('currentMonthDate', date);
      //     },
      //     value: formValues.currentMonthDate, // moment().toDate(),
      //   }),
      // },
      {
        label: 'Synthetic Roas Vector Avg Months',
        section: SECTION_KEY.INPUT2,
        name: 'roasAvgBy',
        element: SelectInput,
        value: formValues.roasAvgBy,
        required: true,
        isNumberValue: true,
        placeholder: 'Select Value',
        options: getRoasAvgByOptions(),
      },
      {
        label: 'MOB Month',
        section: SECTION_KEY.INPUT2,
        name: 'predictionAvgStartIndexOffset',
        tooltip: 'For example, if you select MOB -2 and the calculation period is 3 months, the average will be taken from MOB -2 to MOB -4',
        element: SelectInput,
        value: formValues.predictionAvgStartIndexOffset,
        required: true,
        isNumberValue: true,
        placeholder: 'Select Value',
        options: getMobMonthOptions(),
      },
      {
        label: '# of Months for Calculations',
        section: SECTION_KEY.INPUT2,
        name: 'predictionAvgBy',
        tooltip: 'Number of months used to calculate the average',
        element: SelectInput,
        value: formValues.predictionAvgBy,
        required: true,
        isNumberValue: true,
        placeholder: 'Select Value',
        options: getNumOfMonthsForCalculationsOptions(),
      },
      {
        label: 'Size of Cohorts',
        section: SECTION_KEY.INPUT2,
        name: 'predictionSize',
        tooltip: 'Select the cohort size you want to use for future predictions',
        element: SelectInput,
        value: formValues.predictionSize,
        required: !formValues.roasCohorts,
        isNumberValue: true,
        placeholder: 'Select Value',
        options: getPredictionSizeOptions(),
      },
      {
        label: 'Number Of Repayments',
        section: SECTION_KEY.REPAYMENT_TERMS,
        name: 'numberOfRepayments',
        element: AnotherNumberInput, // TwNumberInput,
        formatCsv: numberFormat,
        required: true,
        // does not work
        // min: 1,
        // max: 50,
        width: 125,
        onChange: (name, value, fields) => {
          setValue(name, value);
          calculateTrueUp(name, value)
        },
      },
      {
        label: 'True Up',
        tooltip: "True-Up: In each cell, enter the percentage of the amount sold that you want to be repaid during that period. Entering 0% means there is no minimum repayment required for that period",
        section: SECTION_KEY.REPAYMENT_TERMS,
        name: 'isTrueUp',
        element: Checkbox,
        onChange: (name, value, fields) => {
          setValue(name, value);
          calculateTrueUp(name, value)
        },
      },
      {
        label: 'Fee',
        section: SECTION_KEY.REPAYMENT_TERMS,
        name: 'fee',
        formatCsv: percentageFormat,
        element: PercentageNumberInput,
        required: true,
        width: 125,
      },
      {
        label: 'Grace',
        section: SECTION_KEY.REPAYMENT_TERMS,
        name: 'grace',
        element: AnotherNumberInput, // TwNumberInput,
        formatCsv: numberFormat,
        required: true,
        width: 125,
        tooltip:
          'If the Grace period is set to 0, the first repayment will occur in the same month as the funding date.',
      },
    ];

    if(formValues.isTrueUp) {
      // True Up
      [...Array(formValues.numberOfRepayments || 0).keys()].map((i) => {
        allFields.push(
          {
            label: `Payment ${i + 1}`,
            section: SECTION_KEY.TRUE_UP,
            name: `trueUpPayment${i}`,
            element: PercentageNumberInput,
            formatCsv: percentageFormat,
            required: false,
            onChange: (name, value, fields) => {
              if (calculateTrueUp(name, value)) {
                setValue(name, value);
              }
            },
          },
        );
      });
    }

    allFields.push({
      label: `Marketing Factor`,
      section: SECTION_KEY.MARKETING_AND_FUNDING,
      name: `mfFactor`,
      element: PercentageNumberInput,
      required: false,
      onChange: (name, value, fields) => {
        setValue(name, value);
        if (!!value) {
          let lastMobIMarketingValue = getValues([`mob${0}Marketing`])
          for (let i = 1; i < MONTHS_COUNT; i++) {
            lastMobIMarketingValue = lastMobIMarketingValue * (1 + value)
            setValue(`mob${i}Marketing`, lastMobIMarketingValue);
            fields.filter((field => field.name === `mob${i}Marketing`)).forEach((field) => {
              field.onChange(`mob${i}Marketing`, lastMobIMarketingValue, fields)
            });
          }
        }
      },
    },
    {
      label: `Default %`,
      section: SECTION_KEY.MARKETING_AND_FUNDING,
      name: `mfDefaultPercent`,
      element: PercentageNumberInput,
      required: false,
      onChange: (name, value, fields) => {
        setValue(name, value);
        if (!!value) {
          for (let i = 0; i < MONTHS_COUNT; i++) {
            setValue(`mob${i}PercentOfMarketing`, value);
            setValue(`mob${i}RevenueShare`, value);
            fields.filter((field => field.name === `mob${i}PercentOfMarketing`)).forEach((field) => {
              field.onChange(`mob${i}PercentOfMarketing`, value, fields)
            });
            fields.filter((field => field.name === `mob${i}RevenueShare`)).forEach((field) => {
              field.onChange(`mob${i}RevenueShare`, value, fields)
            });
          }
        }
      },
    },
    {
      label: `Revenue Factor`,
      section: SECTION_KEY.MARKETING_AND_FUNDING,
      name: `mfRevenueFactor`,
      tooltip: 'Multiply the % Revenue Share times this percentage',
      element: PercentageNumberInput,
      required: false,
      onChange: (name, value, fields) => {
        setValue(name, value);
        if (!!value) {
          for (let i = 0; i < MONTHS_COUNT; i++) {
            const revenueShare = getValues(`mob${i}RevenueShare`);
            setValue(`mob${i}RevenueShareToCollect`, value * revenueShare);
          }
        }
      },
    },
   );

    // Marketing & Funding
    [...Array(MONTHS_COUNT).keys()].map((i) => {
      allFields.push({
        // label: `(Mob ${i}) Marketing`,
        section: SECTION_KEY.MARKETING_AND_FUNDING,
        name: `mob${i}Marketing`,
        element: CurrencyNumberInput,
        formatCsv: numberFormat,
        required: false,
        onChange: (name, value, fields) => {
          if (value !== null) {
            const percentOfMarketing = getValues(`mob${i}PercentOfMarketing`)
            setValue(name, value);
            setValue(`mob${i}Funding`, value * percentOfMarketing);
          }
        },
      })
      allFields.push({
        // label: `(Mob ${i}) % of  Marketing`,
        section: SECTION_KEY.MARKETING_AND_FUNDING,
        name: `mob${i}PercentOfMarketing`,
        element: PercentageNumberInput,
        formatCsv: percentageFormat,
        required: false,
        onChange: (name, value, fields) => {
          if (value !== null) {
            const marketing = getValues(`mob${i}Marketing`)
            setValue(name, value);
            setValue(`mob${i}Funding`, marketing * value);
          }
        },
      })
      allFields.push({
        // label: `(Mob ${i}) Funding`,
        section: SECTION_KEY.MARKETING_AND_FUNDING,
        name: `mob${i}Funding`,
        element: CurrencyNumberInput,
        formatCsv: numberFormat,
        required: false,
        onChange: (name, value, fields) => {
          if (value !== null) {
            const marketing = getValues(`mob${i}Marketing`)
            setValue(name, value);
            setValue(`mob${i}PercentOfMarketing`, value / marketing);
          }
        },
      })
      allFields.push({
        // label: `(Mob ${i}) Revenue Share`,
        section: SECTION_KEY.MARKETING_AND_FUNDING,
        name: `mob${i}RevenueShare`,
        element: PercentageNumberInput,
        formatCsv: percentageFormat,
        required: false,
        onChange: (name, value, fields) => {
          if (value !== null) {
            const revenueFactor = getValues('mfRevenueFactor')
            setValue(name, value);
            setValue(`mob${i}RevenueShareToCollect`, value * revenueFactor);
          }
        },
      })
      allFields.push({
        // label: `(Mob ${i}) Revenue Share`,
        section: SECTION_KEY.MARKETING_AND_FUNDING,
        name: `mob${i}RevenueShareToCollect`,
        element: PercentageNumberInput,
        formatCsv: percentageFormat,
        required: false,
        // onChange: (name, value, fields) => {
        //   if (value !== null) {
        //     const marketing = getValues(`mob${i}RevenueShare`)
        //     setValue(name, value);
        //     setValue(`mob${i}PercentOfMarketing`, value / marketing);
        //   }
        // },
      })

    });

    // setting default values
    useEffect(() => {
      const values = allFields.reduce((acc, field) => {
        if (field.name) {
          acc[field.name] = null;
        }
        return acc;
      }, {});
      // values.currentMonthDate = moment().format('YYYY-MM-DD');
      values.roasAvgBy = "3";
      values.predictionAvgStartIndexOffset = "2";
      values.predictionAvgBy = "3";
      values.predictionSize = "12";
      values.mfFactor = 0.0;
      values.mfDefaultPercent = 0.5;
      values.mfRevenueFactor = 1.0;

      // change if values above has changed!
      for (let i = 0; i < MONTHS_COUNT; i++) {
        values[`mob${i}Marketing`] = 0;
        values[`mob${i}PercentOfMarketing`] = values.mfDefaultPercent;
        values[`mob${i}Funding`] = 0;
        values[`mob${i}RevenueShare`] = values.mfDefaultPercent;
        values[`mob${i}RevenueShareToCollect`] = values.mfDefaultPercent;
      }

      reset(values);
    }, []);

    useEffect(() => {
      if (formValues.customerId) {
        loadScenario({
          customerId: formValues.customerId,
          scenarioType: SCENARIO_TYPE.DEFAULT,
          // scenarioType: SCENARIO_TYPE.BULL,
        });
      }
      onCustomerChange();
    }, [formValues.customerId]);

    useImperativeHandle(
      ref,
      () => {
        return {
          generateCsvData: () => {
            let csvData = allFields.reduce((result, field) => {
              if (field.formatCsv) {
                result.push([
                  field.label,
                  field.formatCsv(formValues[field.name]),
                ]);
              }
              return result;
            }, []);

            const driversCsv = refDrivers.current.generateCsvData();
            return [...csvData, ...driversCsv];
          },
        };
      },
      [formValues]
    );

    const loadScenario = async ({ customerId, scenarioType }) => {
      try {
        setLoading(true);
        let _scenario = null;
        let settings = null;
        if (scenarioType && scenarioType !== SCENARIO_TYPE.DEFAULT) {
          _scenario = await getCashflowModelScenarioApi({
            customerId,
            scenarioType,
          });
        }
        settings = _scenario?.settings;
        if (_scenario) {
          setComments(_scenario.comments)
        }

        const _marketingVector = await getCashflowModelMarketingVectorApi({
          customerId,
        });

        if (!settings) {
          settings = allFields.reduce((acc, field) => {
            if (field.name && field.name !== 'customerId') {
              acc[field.name] = null;
            }
            return acc;
          }, {});
        }

        let spendsDict = Object.assign({}, ..._marketingVector.spends.map((x) => ({[x.date.year * 100 + x.date.month]: x.total})));

        for (let i = 0; i < MONTHS_COUNT; i++) {
            const date = firstOfMonthIn(i);
            settings[`mob${i}Marketing`] ??= spendsDict[date.getFullYear()*100 + date.getMonth() + 1] || 0;
            settings[`mob${i}PercentOfMarketing`] ??= formValues.mfDefaultPercent;
            settings[`mob${i}Funding`] ??= settings[`mob${i}Marketing`] * settings[`mob${i}PercentOfMarketing`];
            settings[`mob${i}RevenueShare`] ??= settings[`mob${i}PercentOfMarketing`];
            settings[`mob${i}RevenueShareToCollect`] ??= settings[`mob${i}RevenueShare`];
        }

        // preserve and reload the values that are not in the scenario
        const roasAvgBy = formValues.roasAvgBy;
        const predictionAvgStartIndexOffset = formValues.predictionAvgStartIndexOffset;
        const predictionAvgBy = formValues.predictionAvgBy;
        const predictionSize = formValues.predictionSize;
        const roasCohorts = formValues.roasCohorts;
        const accounting = formValues.accounting;
        const cashOverride = formValues.cashOverride;
        const numberOfRepayments = formValues.numberOfRepayments;
        const isTrueUp = formValues.isTrueUp;
        const mfFactor = formValues.mfFactor;
        const mfDefaultPercent = formValues.mfDefaultPercent;
        const mfRevenueFactor = formValues.mfRevenueFactor;

        const newValues = { customerId,  ...settings };

        if(newValues.roasAvgBy === null)
          newValues.roasAvgBy = roasAvgBy;
        if(newValues.predictionAvgStartIndexOffset === null)
          newValues.predictionAvgStartIndexOffset = predictionAvgStartIndexOffset;
        if(newValues.predictionAvgBy === null)
          newValues.predictionAvgBy = predictionAvgBy;
        if(newValues.predictionSize === null)
          newValues.predictionSize = predictionSize;
        if(newValues.roasCohorts === null)
          newValues.roasCohorts = roasCohorts;
        if(newValues.accounting === null)
          newValues.accounting = accounting;
        if(newValues.cashOverride === null)
          newValues.cashOverride = cashOverride;
        if(newValues.numberOfRepayments === null)
          newValues.numberOfRepayments = numberOfRepayments;
        if(newValues.isTrueUp === null)
          newValues.isTrueUp = isTrueUp;
        if(newValues.mfFactor === null)
          newValues.mfFactor = mfFactor;
        if(newValues.mfDefaultPercent === null)
          newValues.mfDefaultPercent = mfDefaultPercent;
        if(newValues.mfRevenueFactor === null)
          newValues.mfRevenueFactor = mfRevenueFactor;

        if(settings.trueUpPayments) {
          settings.trueUpPayments.map((x, i) => {
            newValues[`trueUpPayment${i}`] = x
          });
          delete settings.trueUpPayments;
        }

        reset(newValues);

        if (_scenario) {
          if(_scenario.drivers) {
            setDrivers(_scenario.drivers);
          } else {
            setDrivers(null);
          }
          setScenario(_scenario);
        } else {
          setDrivers(null);
          setScenario(_scenario);
        }

        if (scenarioType && scenarioType !== SCENARIO_TYPE.DEFAULT) {
          setUseScenarioForDrivers(scenarioType !== SCENARIO_TYPE.DEFAULT)
          toast({
            title: 'Success',
            description: `Scenario ${SCENARIO_TYPE_META[scenarioType].label} loaded.`,
            status: 'success',
          });
        } else {
          setUseScenarioForDrivers(false)
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to get default settings.',
          status: 'error',
        });
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const updateScenario = async (scenarioType) => {
      if (!formValues.customerId) {
        return;
      }

      const customerId = formValues.customerId;

      let trueUpPayments = []
      if (!!formValues.numberOfRepayments && formValues.isTrueUp) {
        [...Array(formValues.numberOfRepayments).keys()].map((i) => {
          trueUpPayments.push(formValues[`trueUpPayment${i}`]);
        });
      }

      let settings = {
        // uaSpend: formValues.uaSpend,
        // uaSpendNumberOfIterations: formValues.uaSpendNumberOfIterations,
        // uaSpendFactor: formValues.uaSpendFactor,
        // twelveFundingFromUa: formValues.twelveFundingFromUa,
        // numberOfFunding: formValues.numberOfFunding,

        numberOfRepayments: formValues.numberOfRepayments,
        isTrueUp: formValues.isTrueUp,
        trueUpPayments: trueUpPayments,
        fee: formValues.fee,
        grace: formValues.grace,
        // revenueShare: formValues.revenueShare,

        roasAvgBy: formValues.roasAvgBy,
        predictionAvgStartIndexOffset: formValues.predictionAvgStartIndexOffset,
        predictionAvgBy: formValues.predictionAvgBy,
        predictionSize: formValues.predictionSize,
        mfFactor: formValues.mfFactor,
        mfDefaultPercent: formValues.mfDefaultPercent,
        mfRevenueFactor: formValues.mfRevenueFactor,
        cashOverride: formValues.cashOverride,
        includePreviewInstallments: formValues.includePreviewInstallments,
      };

      for (let i = 0; i < MONTHS_COUNT; i++) {
        settings[`mob${i}Marketing`] = formValues[`mob${i}Marketing`]
        settings[`mob${i}PercentOfMarketing`] = formValues[`mob${i}PercentOfMarketing`]
        settings[`mob${i}Funding`] = formValues[`mob${i}Funding`]
        settings[`mob${i}RevenueShare`] = formValues[`mob${i}RevenueShare`]
        settings[`mob${i}RevenueShareToCollect`] = formValues[`mob${i}RevenueShareToCollect`]
      }

      try {
        setLoading(true);
        await updateCashflowModelScenarioApi({
          customerId,
          scenarioType,
          settings,
          drivers,
          comments: comments,
        });
        toast({
          title: 'Success',
          description: `Scenario ${SCENARIO_TYPE_META[scenarioType].label} updated.`,
          status: 'success',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: `Failed to update scenario.`,
          status: 'error',
        });
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const onSelectCohortsCsv = (file) => {
      const onParseComplete = async (csvData) => {
        let result = [];

        try {
          result = await parseCohortsCsv(csvData);
          setValue('roasCohorts', result);
        } catch (error) {
          setParseCohortsError(error);
        }
      };

      setParseCohortsError(null);
      setValue('roasCohorts', null);
      if (!!file) {
        Papa.parse(file, { complete: onParseComplete });
      }
    };

    const onSelectAccountingCsv = (file) => {
      const onParseComplete = async (csvData) => {
        let result = [];
        try {
          result = await parseAccountingCsv(csvData);
          setValue('accounting', result);
        } catch (error) {
          setParseAccountingError(error);
        }
      };

      setParseAccountingError(null);
      setValue('accounting', null);
      Papa.parse(file, { complete: onParseComplete });
    };

    const isFormFilled = () => {
      const values = cloneDeep(formValues);


      if (values.roasCohorts && values.roasCohorts.length > 0) {
        delete values["predictionSize"]
        delete values["customerId"]
      }

      delete values["trueUpPayments"]

      const notRequiredFields = allFields
        .filter((field) => !field.required)
        .map((field) => field.name);

      notRequiredFields.forEach((field) => {
        delete values[field];
      });

      return !isObjectEmpty(formValues) && hasObjectAllValues(values) && (Object.keys(errors).length === 0)
    };

    const onFormSubmit = (data) => {
      setSelectedScenarioType(null);
      setScenario(null);
      setUseScenarioForDrivers(false);
      onSubmit({ ...data, ...{ drivers } });
    };

    return (
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <OverlaySpinnerWrapper show={loading} isFixed={true}>
          <VStack w={'max'} spacing={6} align={'start'}>
            {SECTION_CONFIG.map((section, index) => {

              if ((!formValues.isTrueUp || (formValues.numberOfRepayments || 0) === 0) && section.key === SECTION_KEY.TRUE_UP) {
                return null;
              }

              return (
                <SectionFields
                  key={index}
                  title={section.title}
                  fields={allFields}
                  sectionKey={section.key}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              );
            })}
          </VStack>
          <Box mt={10}>
            <CashflowModelDrivers
              ref={refDrivers}
              modelResults={modelResults}
              onDriversChange={(d)=>{
                setDrivers(d);
              }}
              roasVector={roasVector}
              initialFactors={selectedScenarioType && scenario?.drivers}
              useInitialFactors={selectedScenarioType && useScenarioForDrivers}
              predictionSize={parseInt(formValues.predictionSize)}
            />
          </Box>
          <Flex mt={10} align={'center'}>
            <FormControl>
              <Checkbox
                id={'includePreviewInstallments'}
                {...register('includePreviewInstallments')}
              >
                Include Preview Installments
              </Checkbox>
            </FormControl>
            <Flex justify={'end'} gap={4}>
              {formValues.customerId /*&& modelResults*/ && (
                <ScenarioSelector
                  value={selectedScenarioType || ''}
                  options={SCENARIO_TYPE_OPTIONS}
                  onLoad={(value) => {
                    setSelectedScenarioType(value);
                    loadScenario({
                      customerId: formValues.customerId,
                      scenarioType: value,
                    });
                  }}
                  comments={selectedScenarioType && comments}
                  onCommentsChange={(value) => {
                    setComments(value);
                    // setScenario((prevScenario) => ({
                    //   ...prevScenario,
                    //   comments: value,
                    // }));
                  }}
                  onSave={async (value) => {
                    setSelectedScenarioType(value);
                    await updateScenario(value);
                  }}
                  // isSaveDisabled={!isFormFilled() || !selectedScenarioType}
                />
              )}
              <Button
                variant={'brand'}
                type={'submit'}
                isDisabled={!isFormFilled()}
              >
                Calculate
              </Button>
            </Flex>
          </Flex>
        </OverlaySpinnerWrapper>
      </form>
    );
  }
);

CashflowModelInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  modelResults: PropTypes.object,
  roasVector: PropTypes.array,
  onCustomerChange: PropTypes.func,
};

export default CashflowModelInputs;
